import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://allchinabuyspreadsheet.icu";

export default function ArticleDetailPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const slug = location.pathname.replace(/^\//, "").split("?")[0];
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 md:pt-28 pb-16">
          <div className="container-custom max-w-3xl mx-auto text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 underline">← Back to Home</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "author": {
      "@type": "Organization",
      "name": "AllChinaBuy Spreadsheet",
    },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified,
    "publisher": {
      "@type": "Organization",
      "name": "AllChinaBuy Spreadsheet",
      "url": siteUrl,
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.title,
        "item": `${siteUrl}/${article.slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}></script>
      <Navbar />

      <main>
        <article className="pt-24 md:pt-28 pb-16">
          <div className="container-custom max-w-3xl mx-auto">
            <header className="text-center mb-10 md:mb-14">
              <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-300 text-xs font-medium text-foreground-600 mb-4">
                {article.tag}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-500 font-light max-w-2xl mx-auto mb-6">
                {article.subtitle}
              </p>
              <div className="flex items-center justify-center gap-3 text-xs text-foreground-500">
                <span>{article.authorName}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.dateModified}</span>
              </div>
            </header>

            <div className="rounded-3xl overflow-hidden mb-12 md:mb-16">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>

            <div className="prose-custom space-y-10 md:space-y-12">
              {article.sections.map((section, si) => (
                <section key={si}>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pi) => (
                    <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {p}
                    </p>
                  ))}
                  {si < article.sections.length - 1 && (
                    <hr className="border-background-200 mt-8" />
                  )}
                </section>
              ))}

              <div className="rounded-2xl bg-background-100/60 p-8 md:p-10 text-center">
                <p className="text-lg md:text-xl text-foreground-600 italic mb-5 font-light">
                  {article.ctaText}
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap"
                >
                  {article.ctaButton}
                </Link>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <Link
                  to="/"
                  className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  ← Back to Homepage
                </Link>
                <nav className="flex flex-wrap items-center gap-3" aria-label="Related articles">
                  <span className="text-xs text-foreground-400">Related:</span>
                  {articles
                    .filter((a) => a.slug !== article.slug)
                    .slice(0, 4)
                    .map((a) => (
                      <Link
                        key={a.slug}
                        to={`/${a.slug}`}
                        className="text-xs text-foreground-600 hover:text-primary-600 transition-colors underline underline-offset-2 whitespace-nowrap"
                      >
                        {a.tag}
                      </Link>
                    ))}
                </nav>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}